import { type ReactNode, useEffect, useId, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { DragScroll } from '@/shared/ui/DragScroll';
import { Icon, type IconName } from '@/shared/ui/Icon';

export interface TabBarItem {
  to: string;
  label: ReactNode;
  icon?: IconName;
  end?: boolean;
  activeWhen?: (pathname: string) => boolean;
}

interface TabBarProps {
  items: TabBarItem[];
  ariaLabel?: string;
}

// 모임 하위 탭 — 아이콘을 라벨 왼쪽에 두어 작은 글씨를 줄인다.
export function TabBar({ items, ariaLabel = '하위 탭' }: TabBarProps) {
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // layoutId는 전역 스코프 — 탭바 인스턴스별로 고유 id를 부여해 서로 간섭하지 않게 한다
  const indicatorId = useId();

  // active 탭이 잘릴 때 자동으로 스크롤해 보여줌
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [pathname]);

  return (
    // v7: 언더라인 탭 → 글래스 세그먼트 필. 활성 필이 스프링으로 미끄러진다.
    <nav ref={navRef} aria-label={ariaLabel} className="-mx-1">
      <DragScroll className="scrollbar-none overflow-x-auto">
        <ul className="flex w-full min-w-max items-stretch gap-1 rounded-full border border-surface-border/70 bg-surface/70 p-1 backdrop-blur">
          {items.map((item) => (
            <li key={item.to} className="min-w-0 flex-1">
              <NavLink
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) => {
                  const active = isActive || item.activeWhen?.(pathname) === true;
                  return `relative flex min-h-[40px] w-full min-w-0 items-center justify-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-colors duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                    active ? 'text-white' : 'text-ink-muted hover:text-ink'
                  }`;
                }}
              >
                {({ isActive }) => {
                  const active = isActive || item.activeWhen?.(pathname) === true;
                  return (
                    <>
                      {active ? (
                        <motion.span
                          layoutId={indicatorId}
                          aria-hidden="true"
                          className="absolute inset-0 rounded-full bg-[image:var(--hero-grad)]"
                          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                        />
                      ) : null}
                      {item.icon ? (
                        <Icon name={item.icon} size={16} className="relative shrink-0" />
                      ) : null}
                      <span className="relative truncate">{item.label}</span>
                    </>
                  );
                }}
              </NavLink>
            </li>
          ))}
        </ul>
      </DragScroll>
    </nav>
  );
}
